/*
 * Featured collection – dynamic vendor grid loader
 * ------------------------------------------------
 * Used by the "Featured collection" section when its "Dynamic product source" is set to
 * "Product vendor collection" on a product page. Because Shopify cannot iterate products
 * by vendor in Liquid, we lazy-load the product's automatic vendor collection
 * (/collections/vendors?q=<Vendor>) and inject its product cards into this section.
 *
 * The vendor route renders the exact same `.f-column.product-column` + <offer-product-card>
 * markup used here, so injected cards upgrade and style automatically.
 */
(function () {
  const SELECTOR = 'featured-collection[data-vendor-url]';

  function extractColumns(doc, currentId, limit) {
    // Prefer cards inside the main product grid to avoid unrelated sections.
    let scope =
      doc.querySelector('.products-list') ||
      doc.querySelector('[id*="product-grid"]') ||
      doc.querySelector('.product-grid') ||
      doc;

    let columns = Array.from(scope.querySelectorAll('.f-column.product-column'));
    if (!columns.length) {
      columns = Array.from(scope.querySelectorAll('.f-column'));
    }

    const result = [];
    for (const col of columns) {
      const card = col.querySelector('[data-product-id]');
      const pid = card ? card.getAttribute('data-product-id') : null;
      if (currentId && pid && pid === String(currentId)) continue; // skip current product
      result.push(col);
      if (result.length >= limit) break;
    }
    return result;
  }

  function load(el) {
    if (el.dataset.vendorLoaded === 'true') return;
    el.dataset.vendorLoaded = 'true';

    const url = el.dataset.vendorUrl;
    const limit = parseInt(el.dataset.vendorLimit, 10) || 10;
    const currentId = el.dataset.currentProductId || '';
    const itemsWrap = el.querySelector('.featured-collection__items');
    if (!url || !itemsWrap) return;

    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const columns = extractColumns(doc, currentId, limit);

        if (!columns.length) {
          // Nothing to show for this vendor – remove the whole section.
          const section = el.closest('.shopify-section');
          if (section) section.remove();
          return;
        }

        itemsWrap.innerHTML = columns.map((c) => c.outerHTML).join('');

        /*
         * The section rendered placeholder slides in Liquid (the product count isn't known
         * server-side), so Swiper initialised against those. Re-wire it to the real cards:
         * a slider only when there are more products than fit in a row, a plain grid
         * otherwise — so a vendor with one product shows one normal-width card rather than
         * a stretched one.
         */
        const perView = parseInt(el.dataset.items, 10);
        const useSlider = Boolean(perView) && columns.length > perView;

        Array.from(itemsWrap.children).forEach((col) => {
          col.classList.add('f-column');
          col.classList.toggle('swiper-slide', useSlider);
        });

        const block = el.closest('[data-id]') || el.parentElement;
        const controls = block ? block.querySelector('.swiper-controls') : null;
        if (controls) controls.style.display = useSlider ? '' : 'none';

        el.enableSlider = useSlider;
        el.dataset.enableSlider = useSlider ? 'true' : 'false';
        if (typeof el.destroySlider === 'function') el.destroySlider();
        if (useSlider && typeof el.init === 'function') el.init();

        // Re-run card height equalization once cards have upgraded.
        if (window.__petmall && typeof window.__petmall.scheduleEqualizeOpcCardHeights === 'function') {
          window.__petmall.scheduleEqualizeOpcCardHeights();
        }
        el.dispatchEvent(new CustomEvent('featured-collection-vendor:loaded', { bubbles: true }));
      })
      .catch((e) => {
        console.error('[featured-collection-vendor]', e);
      });
  }

  function observe(el) {
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              obs.unobserve(entry.target);
              load(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px 600px 0px' }
      );
      io.observe(el);
    } else {
      load(el);
    }
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(observe);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Re-init when sections are re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
